import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Clock, Plus, Pencil, Trash2, X, Plane, Stethoscope, RefreshCcw,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import DriverDetailModal from "@/components/DriverDetailModal";
import VehicleDetailModal from "@/components/VehicleDetailModal";
import DayNav, { dateKey } from "@/components/DayNav";

const fmt = (mins) => {
  if (mins == null) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
};
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("de-DE") : "—");
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("de-DE") : "—");
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (s) => (s ? new Date(s).toISOString() : null);
const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyForm = { driver_id: "", vehicle_id: "", start_time: "", end_time: "", note: "", breaks: [] };
const emptyLeave = { driver_id: "", type: "urlaub", date_from: todayISO(), date_to: todayISO(), note: "" };

export default function WorkTime() {
  const { user } = useAuth();
  const isFahrer = user.role === "fahrer";

  // Data
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [summary, setSummary] = useState({ daily_minutes: 0, weekly_minutes: 0, monthly_minutes: 0 });
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [categories, setCategories] = useState([]);

  // Filters
  const [selectedDriver, setSelectedDriver] = useState("all");
  const [view, setView] = useState("today"); // today | week | month | all
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Quick end-time dialog (for "offen" entries)
  const [endRow, setEndRow] = useState(null);
  const [endValue, setEndValue] = useState("");

  // Manual worktime dialog
  const [wtOpen, setWtOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmDel, setConfirmDel] = useState(null);

  // Leave dialog
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveForm, setLeaveForm] = useState(emptyLeave);

  // Driver detail dialog
  const [detailDriver, setDetailDriver] = useState(null);
  const [detailVehicleId, setDetailVehicleId] = useState(null);

  const load = async () => {
    try {
      const params = selectedDriver !== "all" ? { driver_id: selectedDriver } : {};
      const [a, hRes, l, s] = await Promise.all([
        api.get("/worktimes/active").catch(() => ({ data: null })),
        api.get("/worktimes", { params }),
        api.get("/leaves", { params }),
        api.get("/worktimes/summary", { params }),
      ]);
      setActive(a.data);
      setHistory(hRes.data);
      setLeaves(l.data);
      setSummary(s.data);
      if (!isFahrer) {
        const [d, v, c] = await Promise.all([api.get("/drivers"), api.get("/vehicles"), api.get("/categories")]);
        setDrivers(d.data);
        setVehicles(v.data);
        setCategories(c.data);
      } else {
        setVehicles((await api.get("/vehicles")).data);
      }
    } catch (e) { if (e?.response?.status !== 401) toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [selectedDriver]);

  // ---- Prefill from Arbeitszeitmodell (work-time model) ----
  const pad2 = (n) => String(n).padStart(2, "0");
  const prefillFromModel = (driverId) => {
    const drv = drivers.find((d) => d.id === driverId);
    const cat = categories.find((c) => c.id === drv?.category_id);
    if (!cat?.schedules) return null;
    const now = new Date();
    const wd = (now.getDay() + 6) % 7; // Mon=0 … Sun=6
    const day = cat.schedules[String(wd)];
    if (!day || !day.active) return null;
    const block = (Array.isArray(day.blocks) && day.blocks.length) ? day.blocks[0] : day;
    const dateStr = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
    const mk = (hhmm) => (hhmm ? `${dateStr}T${hhmm}` : "");
    return {
      start_time: mk(block.start_time),
      end_time: mk(block.end_time),
      breaks: (block.breaks || []).filter((b) => b.start && b.end).map((b) => ({ start: mk(b.start), end: mk(b.end) })),
    };
  };

  // ---- Worktime modal ----
  const startCreate = () => { setEditing(null); setForm({ ...emptyForm }); setWtOpen(true); };
  const startEdit = (rec) => {
    setEditing(rec);
    setForm({
      driver_id: rec.driver_id || "",
      vehicle_id: rec.vehicle_id || "",
      start_time: toLocalInput(rec.start_time),
      end_time: toLocalInput(rec.end_time),
      note: rec.note || "",
      breaks: (rec.breaks || []).map((b) => ({ start: toLocalInput(b.start), end: toLocalInput(b.end) })),
    });
    setWtOpen(true);
  };
  const saveWt = async () => {
    if (!form.driver_id) { toast.error("Bitte Fahrer wählen"); return; }
    if (!form.start_time) { toast.error("Bitte Startzeit angeben"); return; }
    const payload = {
      driver_id: form.driver_id,
      vehicle_id: form.vehicle_id || null,
      start_time: fromLocalInput(form.start_time),
      end_time: form.end_time ? fromLocalInput(form.end_time) : null,
      note: form.note || null,
      breaks: (form.breaks || []).filter((b) => b.start).map((b) => ({ start: fromLocalInput(b.start), end: b.end ? fromLocalInput(b.end) : null })),
    };
    try {
      if (editing) { await api.patch(`/worktimes/${editing.id}`, payload); toast.success("Arbeitszeit aktualisiert"); }
      else         { await api.post("/worktimes", payload);                toast.success("Arbeitszeit angelegt");    }
      setWtOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeWt = async () => {
    if (!confirmDel) return;
    try { await api.delete(`/worktimes/${confirmDel.id}`); toast.success("Eintrag gelöscht"); setConfirmDel(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // ---- Quick end-time for "offen" entries ----
  const openEndEdit = (w) => { setEndRow(w); setEndValue(toLocalInput(new Date().toISOString())); };
  const saveEndTime = async () => {
    if (!endRow) return;
    if (!endValue) { toast.error("Bitte Endzeit angeben"); return; }
    const w = endRow;
    const payload = {
      driver_id: w.driver_id,
      vehicle_id: w.vehicle_id || null,
      start_time: w.start_time,
      end_time: fromLocalInput(endValue),
      note: w.note || null,
      breaks: (w.breaks || []).map((b) => ({ start: b.start, end: b.end || null })),
    };
    try { await api.patch(`/worktimes/${w.id}`, payload); toast.success("Endzeit gespeichert"); setEndRow(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const addBreak = () => setForm((f) => ({ ...f, breaks: [...(f.breaks || []), { start: "", end: "" }] }));
  const updBreak = (i, k, v) => setForm((f) => { const arr = [...(f.breaks || [])]; arr[i] = { ...arr[i], [k]: v }; return { ...f, breaks: arr }; });
  const rmBreak = (i) => setForm((f) => ({ ...f, breaks: (f.breaks || []).filter((_, idx) => idx !== i) }));

  // ---- Leave (Urlaub / Krank) ----
  const startLeave = (type) => { setLeaveForm({ ...emptyLeave, type }); setLeaveOpen(true); };
  const saveLeave = async () => {
    if (!leaveForm.driver_id) { toast.error("Bitte Fahrer wählen"); return; }
    try {
      await api.post("/leaves", leaveForm);
      toast.success(leaveForm.type === "urlaub" ? "Urlaub eingetragen" : "Krankheitstag eingetragen");
      setLeaveOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeLeave = async (l) => {
    if (!window.confirm(`${l.type === "urlaub" ? "Urlaub" : "Krankheitstag"} wirklich löschen?`)) return;
    try { await api.delete(`/leaves/${l.id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // ---- Merged + filtered Verlauf ----
  const merged = useMemo(() => {
    const wt = history.map((w) => ({
      kind: "work",
      id: `wt-${w.id}`,
      raw: w,
      date: w.start_time,
    }));
    const lv = leaves.map((l) => ({
      kind: l.type, // "urlaub" | "krank"
      id: `lv-${l.id}`,
      raw: l,
      date: l.date_from + "T00:00:00+00:00",
    }));
    const all = [...wt, ...lv].sort((a, b) => (a.date < b.date ? 1 : -1));

    if (view === "all") return all;
    if (view === "today") {
      const key = dateKey(selectedDate);
      return all.filter((row) => {
        if (row.kind === "work") return dateKey(new Date(row.date)) === key;
        // leave entries: include if selected day is within the leave range
        return row.raw.date_from <= key && key <= row.raw.date_to;
      });
    }
    const now = new Date();
    const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dow = (dayStart.getDay() + 6) % 7; // Monday=0
    const weekStart = new Date(dayStart.getTime() - dow * 86400000);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const since = view === "week" ? weekStart : monthStart;
    return all.filter((row) => {
      if (row.kind === "work") return new Date(row.date) >= since;
      // For leave entries: include if leave period overlaps with [since, +infinity)
      const dto = new Date(row.raw.date_to + "T23:59:59");
      return dto >= since;
    });
  }, [history, leaves, view, selectedDate]);

  const dayTotalMinutes = useMemo(() => {
    const key = dateKey(selectedDate);
    return history
      .filter((w) => w.end_time && dateKey(new Date(w.start_time)) === key)
      .reduce((s, w) => s + (w.duration_minutes || 0), 0);
  }, [history, selectedDate]);

  const openDriverDetail = (driverId) => {
    const d = drivers.find((x) => x.id === driverId);
    if (d) setDetailDriver(d);
  };
  const detailVehicle = useMemo(() => vehicles.find((v) => v.id === detailVehicleId) || null, [vehicles, detailVehicleId]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl heading font-semibold">Arbeitszeiterfassung</h1>
          <p className="text-sm text-muted-foreground">
            {isFahrer ? "Übersicht deiner erfassten Arbeitszeiten, Pausen und Urlaubs- bzw. Krankheitstage." : "Übersicht der Arbeitszeiten. Arbeitszeiten entstehen automatisch aus den Fahrten der Wochenplanung (auch rückwirkend) – hier werden nur Urlaub/Krank gepflegt."}
          </p>
        </div>
        {!isFahrer && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => startLeave("urlaub")} variant="outline" className="border-sky-300 text-sky-700 dark:text-sky-300" data-testid="leave-create-urlaub">
              <Plane className="h-4 w-4 mr-1" /> Neuer Urlaub
            </Button>
            <Button onClick={() => startLeave("krank")} variant="outline" className="border-orange-300 text-orange-700 dark:text-orange-300" data-testid="leave-create-krank">
              <Stethoscope className="h-4 w-4 mr-1" /> Neuer Krankheitstag
            </Button>
            <Button onClick={load} variant="ghost" size="icon" aria-label="Aktualisieren" data-testid="worktime-refresh">
              <RefreshCcw className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Filter */}
      {!isFahrer && (
        <Card className="p-3">
          <div className="flex gap-3 items-center flex-wrap">
            <span className="text-sm text-muted-foreground">Fahrer:</span>
            <Select value={selectedDriver} onValueChange={setSelectedDriver}>
              <SelectTrigger className="w-64" data-testid="worktime-driver-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Fahrer</SelectItem>
                {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.firstname} {d.lastname}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </Card>
      )}

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="text-xs uppercase text-muted-foreground tracking-wider">Heute</div>
          <div className="mt-1 text-3xl heading font-semibold tabular-nums" data-testid="worktime-summary-daily">{fmt(summary.daily_minutes)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase text-muted-foreground tracking-wider">Diese Woche</div>
          <div className="mt-1 text-3xl heading font-semibold tabular-nums" data-testid="worktime-summary-weekly">{fmt(summary.weekly_minutes)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase text-muted-foreground tracking-wider">Dieser Monat</div>
          <div className="mt-1 text-3xl heading font-semibold tabular-nums" data-testid="worktime-summary-monthly">{fmt(summary.monthly_minutes)}</div>
        </Card>
      </div>

      {/* Verlauf */}
      <Card className="overflow-x-auto">
        <div className="p-4 border-b border-border space-y-3">
          <div className="flex items-center gap-4 flex-wrap">
            <h2 className="text-base font-semibold heading">Verlauf</h2>
            <Tabs value={view} onValueChange={setView} className="ml-auto">
              <TabsList data-testid="verlauf-tabs">
                <TabsTrigger value="today" data-testid="verlauf-tab-today">Tag</TabsTrigger>
                <TabsTrigger value="week" data-testid="verlauf-tab-week">Woche</TabsTrigger>
                <TabsTrigger value="month" data-testid="verlauf-tab-month">Monat</TabsTrigger>
                <TabsTrigger value="all" data-testid="verlauf-tab-all">Alle</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          {view === "today" && (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <DayNav date={selectedDate} onChange={setSelectedDate} testid="verlauf-daynav" />
              <span className="text-sm text-muted-foreground">
                Gesamt: <span className="font-semibold text-foreground tabular-nums" data-testid="verlauf-day-total">{fmt(dayTotalMinutes)}</span>
              </span>
            </div>
          )}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-2">Typ</th>
              <th className="text-left px-4 py-2">Fahrer</th>
              <th className="text-left px-4 py-2">Fahrzeug</th>
              <th className="text-left px-4 py-2">Start</th>
              <th className="text-left px-4 py-2">Ende</th>
              <th className="text-left px-4 py-2">Dauer</th>
              <th className="text-left px-4 py-2">Pause</th>
              <th className="text-left px-4 py-2">Fahrten</th>
              {!isFahrer && <th></th>}
            </tr>
          </thead>
          <tbody>
            {merged.map((row) => {
              if (row.kind === "work") {
                const w = row.raw;
                return (
                  <tr key={row.id} className="border-t border-border row-hover" data-testid={`worktime-row-${w.id}`}>
                    <td className="px-4 py-2">
                      {w.source === "trip" ? (
                        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-200" title="Automatisch aus abgeschlossenen Fahrten">
                          <Clock className="h-3 w-3" /> Fahrten
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-200">
                          <Clock className="h-3 w-3" /> Arbeit
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {w.driver_id ? (
                        <button onClick={() => openDriverDetail(w.driver_id)} className="text-blue-700 dark:text-blue-300 hover:underline" data-testid={`worktime-driver-link-${w.id}`}>
                          {w.driver_name || "—"}
                        </button>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-2">{w.vehicle_id ? (
                      <button onClick={() => setDetailVehicleId(w.vehicle_id)} className="text-blue-700 dark:text-blue-300 hover:underline" data-testid={`worktime-vehicle-link-${w.id}`}>
                        {w.license_plate || w.vehicle_name || "—"}
                      </button>
                    ) : "—"}</td>
                    <td className="px-4 py-2 tabular-nums whitespace-nowrap">{fmtDateTime(w.start_time)}</td>
                    <td className="px-4 py-2 tabular-nums whitespace-nowrap">{w.end_time ? fmtDateTime(w.end_time) : (
                      !isFahrer ? (
                        <button onClick={() => openEndEdit(w)} className="text-emerald-600 font-medium hover:underline cursor-pointer" data-testid={`worktime-end-open-${w.id}`}>offen</button>
                      ) : <span className="text-emerald-600 font-medium">offen</span>
                    )}</td>
                    <td className="px-4 py-2 tabular-nums">{fmt(w.duration_minutes)}</td>
                    <td className="px-4 py-2 tabular-nums text-muted-foreground" data-testid={`worktime-break-${w.id}`}>{fmt(w.break_minutes)}</td>
                    <td className="px-4 py-2 tabular-nums" data-testid={`worktime-tripcount-${w.id}`}>{w.trip_count ?? 0}</td>
                    {!isFahrer && (
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {w.source === "trip" ? (
                          <span className="text-xs text-muted-foreground italic" data-testid={`worktime-auto-${w.id}`}>automatisch</span>
                        ) : (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => startEdit(w)} data-testid={`worktime-edit-${w.id}`}><Pencil className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => setConfirmDel(w)} className="text-red-600" data-testid={`worktime-delete-${w.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                );
              }
              const l = row.raw;
              const isUrlaub = l.type === "urlaub";
              return (
                <tr key={row.id} className="border-t border-border bg-muted/30" data-testid={`leave-row-${l.id}`}>
                  <td className="px-4 py-2">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${isUrlaub ? "bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-200" : "bg-orange-100 text-orange-700 dark:bg-orange-900/60 dark:text-orange-200"}`}>
                      {isUrlaub ? <Plane className="h-3 w-3" /> : <Stethoscope className="h-3 w-3" />}
                      {isUrlaub ? "Urlaub" : "Krank"}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {l.driver_id ? (
                      <button onClick={() => openDriverDetail(l.driver_id)} className="text-blue-700 dark:text-blue-300 hover:underline">
                        {l.driver_name || "—"}
                      </button>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">—</td>
                  <td className="px-4 py-2 tabular-nums whitespace-nowrap">{fmtDate(l.date_from)}</td>
                  <td className="px-4 py-2 tabular-nums whitespace-nowrap">{fmtDate(l.date_to)}</td>
                  <td className="px-4 py-2 text-muted-foreground" colSpan={3}>{l.note || "—"}</td>
                  {!isFahrer && (
                    <td className="px-4 py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => removeLeave(l)} className="text-red-600" data-testid={`leave-delete-${l.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </td>
                  )}
                </tr>
              );
            })}
            {merged.length === 0 && (
              <tr><td colSpan={isFahrer ? 8 : 9} className="px-4 py-6 text-center text-sm text-muted-foreground">Keine Einträge im gewählten Zeitraum.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* Worktime Dialog */}
      <Dialog open={wtOpen} onOpenChange={setWtOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Arbeitszeit bearbeiten" : "Neue Arbeitszeit"}</DialogTitle>
            <DialogDescription>Fahrer, Fahrzeug und Zeiten erfassen. Bei Auswahl eines Fahrers werden Start/Ende und Pausen aus dem Arbeitszeitmodell (heutiger Wochentag) vorausgefüllt – frei änderbar. Dauer wird automatisch berechnet.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Fahrer</Label>
              <Select value={form.driver_id || ""} onValueChange={(v) => setForm((f) => { const next = { ...f, driver_id: v }; if (!editing) { const pf = prefillFromModel(v); if (pf) { next.start_time = pf.start_time; next.end_time = pf.end_time; next.breaks = pf.breaks; } } return next; })}>
                <SelectTrigger data-testid="worktime-driver-select"><SelectValue placeholder="Fahrer wählen" /></SelectTrigger>
                <SelectContent>
                  {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.firstname} {d.lastname}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Fahrzeug (Gefahren mit)</Label>
              <Select value={form.vehicle_id || "none"} onValueChange={(v) => setForm({ ...form, vehicle_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="worktime-vehicle-select"><SelectValue placeholder="Fahrzeug wählen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— keines —</SelectItem>
                  {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.license_plate}{v.vehicle_type ? ` · ${v.vehicle_type}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Start</Label>
              <Input type="datetime-local" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="worktime-start-input" />
            </div>
            <div>
              <Label>Ende (optional)</Label>
              <Input type="datetime-local" value={form.end_time || ""} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="worktime-end-input" />
            </div>
            <div className="col-span-2">
              <Label>Notiz</Label>
              <Textarea rows={2} value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="worktime-note-input" />
            </div>
            <div className="col-span-2 border-t border-border pt-3 mt-1">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-sm font-semibold heading">Pausen</div>
                  <div className="text-xs text-muted-foreground">Pausen werden von der Dauer abgezogen.</div>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={addBreak} data-testid="worktime-break-add">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Pause hinzufügen
                </Button>
              </div>
              {(form.breaks || []).length === 0 && (
                <div className="text-xs text-muted-foreground italic">Keine Pausen erfasst.</div>
              )}
              <div className="space-y-2">
                {(form.breaks || []).map((b, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end" data-testid={`worktime-break-row-${i}`}>
                    <div><Label className="text-xs">Start</Label><Input type="datetime-local" value={b.start || ""} onChange={(e) => updBreak(i, "start", e.target.value)} /></div>
                    <div><Label className="text-xs">Ende</Label><Input type="datetime-local" value={b.end || ""} onChange={(e) => updBreak(i, "end", e.target.value)} /></div>
                    <Button type="button" size="icon" variant="ghost" onClick={() => rmBreak(i)} className="text-red-600" aria-label="Entfernen"><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWtOpen(false)}>Abbrechen</Button>
            <Button onClick={saveWt} data-testid="worktime-save-button">{editing ? "Speichern" : "Anlegen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leave Dialog */}
      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{leaveForm.type === "urlaub" ? "Neuer Urlaub" : "Neuer Krankheitstag"}</DialogTitle>
            <DialogDescription>Mitarbeiter und Zeitraum auswählen. Für diesen Zeitraum wird keine Arbeitszeit erstellt.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Mitarbeiter</Label>
              <Select value={leaveForm.driver_id || ""} onValueChange={(v) => setLeaveForm({ ...leaveForm, driver_id: v })}>
                <SelectTrigger data-testid="leave-driver-select"><SelectValue placeholder="Fahrer wählen" /></SelectTrigger>
                <SelectContent>
                  {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.firstname} {d.lastname}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Von</Label>
                <Input type="date" value={leaveForm.date_from} onChange={(e) => setLeaveForm({ ...leaveForm, date_from: e.target.value })} data-testid="leave-from-input" />
              </div>
              <div>
                <Label>Bis</Label>
                <Input type="date" value={leaveForm.date_to} onChange={(e) => setLeaveForm({ ...leaveForm, date_to: e.target.value })} data-testid="leave-to-input" />
              </div>
            </div>
            <div>
              <Label>Notiz (optional)</Label>
              <Textarea rows={2} value={leaveForm.note || ""} onChange={(e) => setLeaveForm({ ...leaveForm, note: e.target.value })} data-testid="leave-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveOpen(false)}>Abbrechen</Button>
            <Button onClick={saveLeave} data-testid="leave-save-button">Eintragen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick End-Time Dialog */}
      <Dialog open={!!endRow} onOpenChange={(o) => !o && setEndRow(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Endzeit eintragen</DialogTitle>
            <DialogDescription>
              Schicht von <span className="font-semibold">{endRow?.driver_name || "—"}</span> · Start {fmtDateTime(endRow?.start_time)}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Ende der Schicht</Label>
            <Input type="datetime-local" value={endValue} onChange={(e) => setEndValue(e.target.value)} data-testid="worktime-end-quick-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEndRow(null)}>Abbrechen</Button>
            <Button onClick={saveEndTime} data-testid="worktime-end-quick-save">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Worktime Delete Confirm */}
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eintrag löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Die Arbeitszeit von <span className="font-semibold">{confirmDel?.driver_name || "—"}</span> vom {fmtDateTime(confirmDel?.start_time)} wird unwiderruflich entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="worktime-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={removeWt} className="bg-red-600 hover:bg-red-700" data-testid="worktime-delete-confirm">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Driver Detail */}
      <DriverDetailModal driver={detailDriver} open={!!detailDriver} onOpenChange={(o) => !o && setDetailDriver(null)} />
      {/* Vehicle Detail */}
      <VehicleDetailModal vehicle={detailVehicle} open={!!detailVehicle} onOpenChange={(o) => !o && setDetailVehicleId(null)} />
    </div>
  );
}
